import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { LoanPipelineHeaderConfigs } from 'components/LoanPipelineHeaderConfigs'
import type { HeaderRow } from 'components/LoanPipelineHeaderConfigs/HeaderSelectionModal'
import { PlainInput } from 'components/PlainInput'
import { SaveChanges } from 'components/SaveChanges'
import { accountTypes } from 'config/user.type.constants'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { getAdminConfig, setAdminConfig } from 'services'
import { Button, MultiSelect } from 'stories/components'
import { confirm, prompt } from 'utils'

import { defaultProperties } from './constants'

interface ILoanPipelineHeaderConfig {
  title: string
  accountTypes: string[]
  data: HeaderRow[]
  servicing: HeaderRow[]
  fci: HeaderRow[]
  properties: Record<string, any>
}

export const LoanPipelineHeadersByAccountTypes = () => {
  const [isLoading, setLoading] = useState(true)
  const [data, setData] = useState<ILoanPipelineHeaderConfig[]>([])
  const [isChanged, setChanged] = useState(false)

  useEffect(() => {
    const initData = async () => {
      setLoading(true)
      try {
        const config = await getAdminConfig('loanPipelineHeaders')
        setData(config)
      } catch (error) {
      } finally {
        setLoading(false)
      }
    }

    initData()
  }, [])

  const onChange = (index: number, field: keyof ILoanPipelineHeaderConfig, value: any) => {
    const newData = cloneDeep(data)
    ;(newData[index] as any)[field] = value
    setData(newData)
    setChanged(true)
  }

  const onAdd = async () => {
    const title = await prompt('Please enter the configuration title')
    if (!title) return

    const newData = cloneDeep(data)
    newData.push({
      title: title.trim(),
      accountTypes: [],
      data: [],
      servicing: [],
      fci: [],
      properties: {
        ...defaultProperties(),
      },
    })
    setData(newData)
    setChanged(true)
  }

  const onRemove = async (index: number) => {
    if (index === 0) {
      toast('Cannot remove the default configuration', { type: 'error' })
      return
    }

    const result = await confirm('Are you sure you want to remove this configuration?')
    if (!result) return

    const newData = cloneDeep(data)
    newData.splice(index, 1)
    setData(newData)
    setChanged(true)
  }

  const onSubmit = async () => {
    setLoading(true)
    try {
      await setAdminConfig('loanPipelineHeaders', data)
      setChanged(false)
      toast('Loan Pipeline Headers Configuration has been saved.', { type: 'info' })
    } catch (error) {
      console.error('Error saving config:', error)
      toast('Error saving configuration', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const onHeaderConfigSave = (index: number, headerData: HeaderRow[], type: 'data' | 'servicing' | 'fci') => {
    onChange(index, type, headerData)
  }

  const renderItem = (config: ILoanPipelineHeaderConfig, index: number) => {
    const isDefault = index === 0
    return (
      <div key={index} className="border rounded-lg p-6 bg-white shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-4 flex-1">
            <div className="w-64">
              <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
              <PlainInput
                value={config.title}
                content={config.title}
                onChange={(value: string) => onChange(index, 'title', value)}
                disabled={isDefault}
              />
            </div>
            {!isDefault && (
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">Account Types</label>
                <MultiSelect
                  id={`config-${index}-accountType`}
                  title="Account Type"
                  options={accountTypes}
                  value={config.accountTypes || []}
                  onChange={(selected) => onChange(index, 'accountTypes', selected)}
                  disabled={isDefault}
                  className="w-full"
                />
              </div>
            )}
            {!isDefault && (
              <button
                className="text-red-500 hover:text-red-700 p-2"
                onClick={() => onRemove(index)}
                title="Remove configuration"
              >
                <TrashIcon className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        <div className="mt-6">
          <p className="font-bold">Origination Headers</p>
          <LoanPipelineHeaderConfigs
            autoSave
            values={config.data || []}
            setLoading={setLoading}
            onSave={(headerData) => onHeaderConfigSave(index, headerData, 'data')}
          />
        </div>
        <div className="mt-4">
          <p className="font-bold">Servicing Headers</p>
          <LoanPipelineHeaderConfigs
            autoSave
            values={config.servicing || []}
            setLoading={setLoading}
            onSave={(headerData) => onHeaderConfigSave(index, headerData, 'servicing')}
          />
        </div>
        <div className="mt-4">
          <p className="font-bold">FCI Headers</p>
          <p className="text-sm text-gray-500 mb-2">
            Loan #, Borrower / Property Address, Purpose / Product, and Tags are always shown. Configure default FCI
            columns by account type below.
          </p>
          <LoanPipelineHeaderConfigs
            autoSave
            values={config.fci || []}
            setLoading={setLoading}
            onSave={(headerData) => onHeaderConfigSave(index, headerData, 'fci')}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="relative">
      <LayoutLoading show={isLoading} />
      <SaveChanges show={isChanged} onSave={onSubmit} />

      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-semibold">Loan Pipeline Headers by Account Types</h2>
        <Button link onClick={onAdd}>
          <span className="flex gap-2 items-center">
            <PlusIcon className="w-5 h-5" />
            Add Configuration
          </span>
        </Button>
      </div>

      <div className="space-y-8">{data.map((config, index) => renderItem(config, index))}</div>
    </div>
  )
}
